import { z } from 'zod';
import { eventNameSchema } from './event';

export const campaignStatusSchema = z.enum(['draft', 'active', 'paused', 'archived']);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

// Arbitration when two campaigns match at once — one experience on screen at a time, resolved by a
// priority queue in the SDK, not a field in the IR (v1 plan §"Campaign priority"). It ships in the
// v1.0.0 binary, so it must be on the WIRE before the first SDK tag pins: hence bundleSchema, now,
// while there is still no pinned consumer to break.
export const campaignPrioritySchema = z.enum(['active', 'passive', 'time_sensitive']);
export type CampaignPriority = z.infer<typeof campaignPrioritySchema>;

const isoDatetimeSchema = z.string().datetime();

export const campaignScheduleSchema = z.object({
  start: isoDatetimeSchema,
  end: isoDatetimeSchema.optional(),
});
export type CampaignSchedule = z.infer<typeof campaignScheduleSchema>;

// Trigger config (§6): when the SDK's local decision engine should evaluate
// this campaign. Data-only, same "no eval" stance as rules.ts/experience.ts.
export const campaignTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('always') }),
  z.object({ type: z.literal('on_event'), event: eventNameSchema }),
  z.object({ type: z.literal('on_screen'), screen: z.string().min(1) }),
]);
export type CampaignTrigger = z.infer<typeof campaignTriggerSchema>;

// ---------------------------------------------------------------------------- placement

/** A slot the HOST app reserved in its own screen. The string is a contract between the author (who
 *  types it in the dashboard) and the engineer (who wrote `PHQInlineView(slot: "home_hero")`), so it
 *  is deliberately a plain identifier and not an id we mint — nobody wants to copy a UUID into a
 *  storyboard. Bounded and charset-restricted because it crosses into four native SDKs. */
export const slotSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9_]+$/, 'a slot is lowercase letters, digits and underscores');

/** WHERE a campaign renders — and the reason it lives on the CAMPAIGN and not on the document: the
 *  experience says HOW it is presented (`surface`), the campaign says WHERE. One authored strip can
 *  then run in `home_hero` this week and `wallet_top` next, without a second copy of the design.
 *
 *  It is a tagged union so a future placement (a push notification, a widget, an email) slots in
 *  with no schema break — the same shape `repeat.source` and `image.source` already use. */
export const campaignPlacementSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('overlay') }),
  z.object({ kind: z.literal('inline'), slot: slotSchema }),
]);
export type CampaignPlacement = z.infer<typeof campaignPlacementSchema>;

// session_limit enforced on-device against the cached bundle;
// cross_session_limit enforced server-side via KV (§7 step 5).
export const frequencyCapSchema = z.object({
  session_limit: z.number().int().nonnegative(),
  cross_session_limit: z.number().int().nonnegative(),
  window_days: z.number().int().positive().max(90),
});
export type FrequencyCap = z.infer<typeof frequencyCapSchema>;
